import type {
  ChartAlertItem,
  ChartDrawingItem,
  ChartTemplateItem,
  MobileDrawingToolbarPosition,
  PaperTradingOrderItem,
  RuntimeStatusOptions,
} from './types';
import type { ProviderName } from './lib/providerRouting';

interface ChartRuntimeStorageSnapshot {
  version: 2;
  alerts: ChartAlertItem[];
  drawings: ChartDrawingItem[];
  orders: PaperTradingOrderItem[];
  templates: ChartTemplateItem[];
  runtimeStatus: RuntimeStatusOptions;
}

interface ChartAlertsStorageSnapshot {
  version: 1;
  alerts: ChartAlertItem[];
}

interface RuntimeStorageLike {
  getItem(key: string): string | null;
  key(index: number): string | null;
  length: number;
}

export interface RuntimeStorageDrawingMigrationResult {
  drawings: ChartDrawingItem[];
  updates: Array<{
    storageKey: string;
    value: string;
  }>;
}

const CHART_RUNTIME_STORAGE_VERSION = 2;
const LEGACY_CHART_RUNTIME_STORAGE_VERSION = 1;
const CHART_ALERTS_STORAGE_VERSION = 1;
const CHART_ALERTS_STORAGE_KEY = `marketproject:chart-alerts:v${CHART_ALERTS_STORAGE_VERSION}`;

const DEFAULT_RUNTIME_STATUS_OPTIONS: RuntimeStatusOptions = {
  showMarketStatus: true,
  showBuySellButtons: true,
  showIndicatorTitles: true,
  priceScaleSide: 'right',
  lockPriceToBarRatio: false,
  verticalCursorLockTime: null,
  mobileDrawingToolbarPosition: null,
  mobileDrawingToolbarCollapsed: false,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeMobileDrawingToolbarPosition(value: unknown): MobileDrawingToolbarPosition | null {
  if (!isRecord(value) || typeof value.x !== 'number' || typeof value.y !== 'number') {
    return null;
  }

  if (!Number.isFinite(value.x) || !Number.isFinite(value.y)) {
    return null;
  }

  return {
    x: value.x,
    y: value.y,
  };
}

function normalizeRuntimeStatusOptions(value: unknown): RuntimeStatusOptions {
  if (!isRecord(value)) {
    return DEFAULT_RUNTIME_STATUS_OPTIONS;
  }

  return {
    showMarketStatus:
      typeof value.showMarketStatus === 'boolean'
        ? value.showMarketStatus
        : DEFAULT_RUNTIME_STATUS_OPTIONS.showMarketStatus,
    showBuySellButtons:
      typeof value.showBuySellButtons === 'boolean'
        ? value.showBuySellButtons
        : DEFAULT_RUNTIME_STATUS_OPTIONS.showBuySellButtons,
    showIndicatorTitles:
      typeof value.showIndicatorTitles === 'boolean'
        ? value.showIndicatorTitles
        : DEFAULT_RUNTIME_STATUS_OPTIONS.showIndicatorTitles,
    priceScaleSide:
      value.priceScaleSide === 'left' || value.priceScaleSide === 'right'
        ? value.priceScaleSide
        : DEFAULT_RUNTIME_STATUS_OPTIONS.priceScaleSide,
    lockPriceToBarRatio:
      typeof value.lockPriceToBarRatio === 'boolean'
        ? value.lockPriceToBarRatio
        : DEFAULT_RUNTIME_STATUS_OPTIONS.lockPriceToBarRatio,
    verticalCursorLockTime:
      typeof value.verticalCursorLockTime === 'number' && Number.isFinite(value.verticalCursorLockTime)
        ? value.verticalCursorLockTime
        : null,
    mobileDrawingToolbarPosition: normalizeMobileDrawingToolbarPosition(value.mobileDrawingToolbarPosition),
    mobileDrawingToolbarCollapsed:
      typeof value.mobileDrawingToolbarCollapsed === 'boolean'
        ? value.mobileDrawingToolbarCollapsed
        : DEFAULT_RUNTIME_STATUS_OPTIONS.mobileDrawingToolbarCollapsed,
  };
}

export function isProviderName(value: unknown): value is ProviderName {
  return (
    value === 'bitmex' ||
    value === 'binance' ||
    value === 'kraken' ||
    value === 'twelvedata' ||
    value === 'alphavantage' ||
    value === 'oanda'
  );
}

function normalizeChartAlertItem(value: unknown): ChartAlertItem | null {
  if (!isRecord(value)) {
    return null;
  }

  if (!isProviderName(value.provider) || typeof value.symbol !== 'string' || value.symbol.trim().length === 0) {
    return null;
  }

  return value as unknown as ChartAlertItem;
}

function normalizeChartDrawingItem(value: unknown): ChartDrawingItem | null {
  if (!isRecord(value)) {
    return null;
  }

  if (
    typeof value.id !== 'string' ||
    !isProviderName(value.provider) ||
    typeof value.symbol !== 'string' ||
    value.symbol.trim().length === 0 ||
    (value.kind !== 'horizontalLine' &&
      value.kind !== 'horizontalRay' &&
      value.kind !== 'trendLine' &&
      value.kind !== 'ray' &&
      value.kind !== 'extendedLine' &&
      value.kind !== 'rectangle' &&
      value.kind !== 'parallelChannel' &&
      value.kind !== 'fibRetracement' &&
      value.kind !== 'text') ||
    !Array.isArray(value.anchors) ||
    typeof value.visible !== 'boolean' ||
    typeof value.locked !== 'boolean' ||
    typeof value.createdAt !== 'string' ||
    typeof value.updatedAt !== 'string' ||
    !isRecord(value.style)
  ) {
    return null;
  }

  const anchors = value.anchors.flatMap((anchor) => {
    if (!isRecord(anchor) || typeof anchor.time !== 'number' || typeof anchor.price !== 'number') {
      return [];
    }
    if (!Number.isFinite(anchor.time) || !Number.isFinite(anchor.price)) {
      return [];
    }
    return [{ time: anchor.time, price: anchor.price }];
  });

  if (anchors.length === 0) {
    return null;
  }

  const strokeWidth =
    value.style.strokeWidth === 1 ||
    value.style.strokeWidth === 2 ||
    value.style.strokeWidth === 3 ||
    value.style.strokeWidth === 4
      ? value.style.strokeWidth
      : 2;
  const lineStyle =
    value.style.lineStyle === 'solid' || value.style.lineStyle === 'dashed' || value.style.lineStyle === 'dotted'
      ? value.style.lineStyle
      : 'solid';
  const textSize = typeof value.style.textSize === 'number' && Number.isFinite(value.style.textSize)
    ? Math.max(10, Math.min(24, Math.round(value.style.textSize)))
    : 12;

  return {
    id: value.id,
    provider: value.provider,
    symbol: value.symbol,
    kind: value.kind,
    anchors,
    visible: value.visible,
    locked: value.locked,
    text: typeof value.text === 'string' && value.text.trim().length > 0 ? value.text : null,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    style: {
      strokeColor: typeof value.style.strokeColor === 'string' ? value.style.strokeColor : '#7dd3fc',
      strokeWidth,
      lineStyle,
      fillColor: typeof value.style.fillColor === 'string' ? value.style.fillColor : null,
      textColor: typeof value.style.textColor === 'string' ? value.style.textColor : '#e2e8f0',
      textSize,
    },
  };
}

export function normalizeChartDrawingItems(value: unknown): ChartDrawingItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const normalized = normalizeChartDrawingItem(item);
    return normalized ? [normalized] : [];
  });
}

export function normalizeChartAlertItems(value: unknown): ChartAlertItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const normalized = normalizeChartAlertItem(item);
    return normalized ? [normalized] : [];
  });
}

function normalizeChartTemplateItem(value: unknown): ChartTemplateItem | null {
  if (!isRecord(value)) {
    return null;
  }

  if (!isProviderName(value.provider) || typeof value.symbol !== 'string' || value.symbol.trim().length === 0) {
    return null;
  }

  if (
    typeof value.id !== 'string' ||
    typeof value.name !== 'string' ||
    typeof value.createdAt !== 'string' ||
    !isRecord(value.chartSettings) ||
    !isRecord(value.statusOptions)
  ) {
    return null;
  }

  return {
    id: value.id,
    provider: value.provider,
    symbol: value.symbol,
    name: value.name,
    createdAt: value.createdAt,
    chartSettings: value.chartSettings as unknown as ChartTemplateItem['chartSettings'],
    statusOptions: normalizeRuntimeStatusOptions(value.statusOptions),
  };
}

export function normalizeChartTemplateItems(value: unknown): ChartTemplateItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const normalized = normalizeChartTemplateItem(item);
    return normalized ? [normalized] : [];
  });
}

function normalizePaperTradingOrderItem(value: unknown): PaperTradingOrderItem | null {
  if (!isRecord(value)) {
    return null;
  }

  if (
    typeof value.id !== 'string' ||
    !isProviderName(value.provider) ||
    typeof value.symbol !== 'string' ||
    value.symbol.trim().length === 0 ||
    (value.side !== 'buy' && value.side !== 'sell') ||
    (value.orderType !== 'limit' && value.orderType !== 'stop' && value.orderType !== 'market') ||
    typeof value.price !== 'number' ||
    !Number.isFinite(value.price) ||
    typeof value.quantity !== 'number' ||
    !Number.isFinite(value.quantity) ||
    typeof value.createdAt !== 'string' ||
    (value.status !== 'working' && value.status !== 'filled' && value.status !== 'cancelled')
  ) {
    return null;
  }

  return value as unknown as PaperTradingOrderItem;
}

export function normalizePaperTradingOrderItems(value: unknown): PaperTradingOrderItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const normalized = normalizePaperTradingOrderItem(item);
    return normalized ? [normalized] : [];
  });
}

export function createRuntimeStorageKey(provider: ProviderName, symbol: string): string {
  return `marketproject:chart-runtime:v${CHART_RUNTIME_STORAGE_VERSION}:${provider}:${symbol}`;
}

export function createLegacyRuntimeStorageKey(symbol: string): string {
  return `marketproject:chart-runtime:v${LEGACY_CHART_RUNTIME_STORAGE_VERSION}:${symbol}`;
}

export function createAlertsStorageKey(): string {
  return CHART_ALERTS_STORAGE_KEY;
}

export function parseRuntimeStorageSnapshot(raw: string | null): ChartRuntimeStorageSnapshot | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ChartRuntimeStorageSnapshot>;
    return {
      version: 2,
      alerts: normalizeChartAlertItems(parsed.alerts),
      drawings: normalizeChartDrawingItems(parsed.drawings),
      orders: normalizePaperTradingOrderItems(parsed.orders),
      templates: normalizeChartTemplateItems(parsed.templates),
      runtimeStatus: normalizeRuntimeStatusOptions(parsed.runtimeStatus),
    };
  } catch {
    return null;
  }
}

export function parseAlertsStorageSnapshot(raw: string | null): ChartAlertsStorageSnapshot | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ChartAlertsStorageSnapshot>;
    return {
      version: 1,
      alerts: normalizeChartAlertItems(parsed.alerts),
    };
  } catch {
    return null;
  }
}

export function collectRuntimeStorageDrawingMigration(
  storage: RuntimeStorageLike,
): RuntimeStorageDrawingMigrationResult {
  const migratedDrawingsById = new Map<string, ChartDrawingItem>();
  const updates: RuntimeStorageDrawingMigrationResult['updates'] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const storageKey = storage.key(index);
    if (!storageKey) {
      continue;
    }

    const isLegacy = storageKey.startsWith('marketproject:chart-runtime:v1:');
    const isCurrent = storageKey.startsWith('marketproject:chart-runtime:v2:');
    if (!isLegacy && !isCurrent) {
      continue;
    }

    const snapshot = parseRuntimeStorageSnapshot(storage.getItem(storageKey));
    if (!snapshot || snapshot.drawings.length === 0) {
      continue;
    }

    for (const drawing of snapshot.drawings) {
      const drawingKey = `${drawing.id}:${drawing.provider}:${drawing.symbol}`;
      if (!migratedDrawingsById.has(drawingKey)) {
        migratedDrawingsById.set(drawingKey, drawing);
      }
    }

    updates.push({
      storageKey,
      value: JSON.stringify({
        version: isLegacy ? 1 : 2,
        alerts: snapshot.alerts,
        drawings: [],
        orders: snapshot.orders,
        templates: snapshot.templates,
        runtimeStatus: snapshot.runtimeStatus,
      }),
    });
  }

  return {
    drawings: Array.from(migratedDrawingsById.values()),
    updates,
  };
}
